import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from '../ui';

export type UnitChoice = { id: string; to: string; label: string };

/** From this many units on the switcher gets a search field ("Einheit suchen"). */
export const UNIT_SEARCH_FROM = 7;

/**
 * Unit switcher in the page header of a property (level 2 "Einheiten" and level 3): a button with the open unit (or
 * "Einheit wählen") that opens every unit of the property in house order, with a search field for bigger houses.
 * Replaces the unit list that used to hang under "Einheiten" in the sidebar. Closes on Escape, outside click and
 * navigation; the open (or last opened) unit is marked.
 */
export function UnitSwitch({ units, activeId, here = false }: { units: UnitChoice[]; activeId?: string; /** the active unit is the page shown (level 3), not only the last opened one */ here?: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const current = units.find((u) => u.id === activeId);
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) {
      setQ('');
      return;
    }
    const onDown = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    requestAnimationFrame(() => box.current?.querySelector<HTMLElement>('.unit-switch-pop input, .unit-switch-pop a')?.focus());
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  if (!units.length) return null;
  const needle = q.trim().toLocaleLowerCase();
  const shown = needle ? units.filter((u) => u.label.toLocaleLowerCase().includes(needle)) : units;
  return (
    <div
      className="unit-switch"
      ref={box}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          setOpen(false);
          btn.current?.focus();
          return;
        }
        // arrow keys walk through the search field and the units (Home/End jump), like a menu
        if (!open || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
        const items = [...(box.current?.querySelectorAll<HTMLElement>('.unit-switch-pop input, .unit-switch-pop a') ?? [])];
        if (!items.length) return;
        e.preventDefault();
        const at = items.indexOf(document.activeElement as HTMLElement);
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : e.key === 'ArrowDown' ? Math.min(items.length - 1, at + 1) : Math.max(0, at - 1);
        items[next]?.focus();
      }}
      // Tab out of the list closes it (focus never stays in a hidden popover)
      onBlur={(e) => {
        if (open && e.relatedTarget && !box.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <button ref={btn} type="button" className="btn secondary sm" aria-label={current ? `${t('workspace.unitSwitch.label')}: ${current.label}` : undefined} aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen((o) => !o)}>
        <Icon name="home" size={16} />
        <span className="truncate">{current ? current.label : t('workspace.unitSwitch.choose')}</span>
        <Icon name="chevronDown" size={16} strokeWidth={1.8} className={`menu-caret ${open ? 'open' : ''}`} />
      </button>
      {open && (
        <div className="unit-switch-pop menu-pop start" id={id} role="group" aria-label={t('workspace.unitSwitch.label')}>
          {units.length >= UNIT_SEARCH_FROM && (
            <input
              type="search"
              className="unit-switch-filter"
              placeholder={t('shell.level.unitFilter')}
              aria-label={t('shell.level.unitFilter')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          )}
          <ul className="unit-switch-list" aria-label={t('shell.level.unitList')}>
            {shown.map((u) => (
              <li key={u.id}>
                <Link to={u.to} aria-current={u.id === activeId && here ? 'page' : undefined} className={u.id === activeId ? 'active' : undefined}>
                  {u.label}
                </Link>
              </li>
            ))}
          </ul>
          {/* announced while typing: "Keine Einheit gefunden" */}
          <p className="unit-switch-empty" role="status">{shown.length === 0 ? t('shell.level.unitNoMatch') : ''}</p>
        </div>
      )}
    </div>
  );
}
